import { Fragment, useState } from "react";
import { useTranslation } from "react-i18next";
import { KONKURSSIPAIVA, VALVONTAPAIVA } from "../constants";

/**
 * Contract terms block shown on Step 2 (edit / "Muokkaa tietoja")
 * under the "1. Velkojatiedot" heading, and on the read-only approved view.
 *
 * Expanded by default showing the whole big text block (the approved
 * view passes defaultExpanded={false}). Clicking the toggle collapses it
 * back to the first rows with a fade-out overlay; the button reads
 * "Näytä vähemmän" while expanded.
 */
export function ContractTerms({
  defaultExpanded = true,
}: {
  defaultExpanded?: boolean;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div className="bg-white rounded-2xl shadow-xl shadow-gray-200/60 border border-gray-100 overflow-hidden">
      {/* Collapsible text area */}
      <div className="relative">
        <div
          className={`px-5 pt-5 transition-all duration-300 ${
            expanded ? "max-h-none" : "max-h-48 overflow-hidden"
          }`}
        >
          <div className="space-y-4 text-[13px] leading-relaxed text-gray-600">
            {/* ── Section 1 ─────────────────────────────────────── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("contractTerms.general.title")}
              </h3>
              <p>{t("contractTerms.general.intro", { date: VALVONTAPAIVA })}</p>
              <p className="mt-3">{t("contractTerms.general.listIntro")}</p>
              <ol className="list-decimal pl-5 space-y-1.5 mt-2">
                <li>
                  {t("contractTerms.general.amount")}
                  <ul className="list-disc pl-5 space-y-1 mt-1">
                    {t("contractTerms.general.amountItems", { returnObjects: true }).map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </li>
                {t("contractTerms.general.items", { returnObjects: true }).map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ol>
              {t("contractTerms.general.paragraphs", { returnObjects: true }).map((text, i) => (
                <p key={i} className="mt-3">{text}</p>
              ))}
            </div>

            {/* ── Section 2 ─────────────────────────────────────── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("contractTerms.prepayment.title")}
              </h3>
              {t("contractTerms.prepayment.paragraphs", {
                returnObjects: true,
                bankruptcyDate: KONKURSSIPAIVA,
              }).map((text, i) => (
                <p key={i} className={i > 0 ? "mt-3" : undefined}>{text}</p>
              ))}
            </div>

            {/* ── Section 3 ─────────────────────────────────────── */}
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">
                {t("contractTerms.instructions.title")}
              </h3>
              <ul className="list-disc pl-5 space-y-3">
                <li>{t("contractTerms.instructions.prefilled")}</li>
                {t("contractTerms.instructions.faq", {
                  returnObjects: true,
                  date: VALVONTAPAIVA,
                }).map(({ question, answer }, i) => (
                  <li key={i}>
                    <span className="font-semibold text-gray-800">{question}</span>{" "}
                    {answer.map((text, j) => (
                      <Fragment key={j}>
                        {j > 0 && <br />}
                        {text}
                      </Fragment>
                    ))}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Fade-out overlay when collapsed */}
        {!expanded && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent" />
        )}
      </div>

      {/* Expand / collapse toggle */}
      <div className="px-5 pb-4 pt-1">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="w-full flex items-center justify-center gap-1.5 py-2.5 text-sm font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-xl border border-blue-100 transition-all duration-200"
        >
          {expanded ? t("contractTerms.showLess") : t("contractTerms.showMore")}
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
